import { useCallback, useEffect, useMemo, useState } from "react";
import { spaceActions } from "redux/Ldb/actions/spaceActions";
import { useAppDispatch, useAppSelector } from "../storeHooks";
import { SpaceTask, SpaceTaskFilters } from "redux/Ldb/types/spaceTypes";
import { useInfiniteScrollV2 } from "hooks/useInfiniteScrollV2";

const generateFilterKey = (filters?: SpaceTaskFilters): string => {
    if (!filters) return "default";

    // Create a stable string representation of the filters
    const filterEntries = Object.entries(filters)
        .filter(([_, value]) => value !== undefined && value !== null && value !== "")
        .sort(([a], [b]) => a.localeCompare(b));

    return filterEntries.length > 0
        ? filterEntries.map(([key, value]) => `${key}:${value}`).join("|")
        : "default";
};

export interface UseGetSpaceTasksReturn {
    isSuccess: boolean;
    isError: boolean;
    isLoading: boolean;
    isFetching: boolean;
    data: {
        tasks: SpaceTask[];
        stats: {
            atRiskTasks: number;
            overdueTasks: number;
            allTasks?: number;
            todoTasks: number;
            completedTasks: number;
        };
    };
    error: any;
    pagination: { currentPage: string; noOfPages: string };
    refetch: () => void;
}

const useGetSpaceTasks = (
    request: { path: { spaceId: string }; params?: SpaceTaskFilters },
    options: {
        skip?: boolean;
        isInfiniteScroll?: boolean;
        scrollContentId?: string;
        refetchOnMount?: boolean;
    } = {}
): UseGetSpaceTasksReturn => {
    const {
        path: { spaceId },
        params: filters,
    } = request;
    const {
        skip = false,
        isInfiniteScroll = false,
        scrollContentId,
        refetchOnMount = false,
    } = options;
    const dispatch = useAppDispatch();

    const filterKey = generateFilterKey(filters);

    const [isLoading, setIsLoading] = useState<Map<string, boolean>>(new Map([[filterKey, false]]));
    const [isFetching, setIsFetching] = useState<Map<string, boolean>>(
        new Map([[filterKey, false]])
    );
    const [isSuccess, setIsSuccess] = useState<Map<string, any>>(new Map([[filterKey, null]]));
    const [loadingIndex, setLoadingIndex] = useState<Map<string, number>>(
        new Map([[filterKey, 0]])
    );

    const {
        fetchingSpaceTasks,
        fetchedSpaceTasksSuccess,
        fetchedSpaceTasksFailure,
        createdSpaceTaskSuccess,
        updatedSpaceTaskSuccess,
        deletedSpaceTaskSuccess,
    } = useAppSelector((state) => state.space);

    const [tasksList, setTasksList] = useState<Map<string, { tasks: SpaceTask[]; stats: any }>>(
        new Map([[filterKey, { tasks: [], stats: null }]])
    );

    const [pagination, setPagination] = useState<
        Map<string, { currentPage: string; noOfPages: string }>
    >(new Map([[filterKey, { currentPage: "1", noOfPages: "" }]]));

    const isFetchingValue = isFetching.get(filterKey) || false;
    const isSuccessValue = isSuccess.get(filterKey);
    const hasMore =
        isSuccessValue?.data?.pagination?.current <
        isSuccessValue?.data?.pagination?.number_of_pages;

    useInfiniteScrollV2(
        useMemo(
            () => ({
                containerId: scrollContentId,
                onLoadMore: () => {
                    if (isInfiniteScroll) {
                        setPagination((prev) => {
                            const newMap = new Map(prev);
                            newMap.set(filterKey, {
                                ...prev.get(filterKey),
                                currentPage: String(Number(prev.get(filterKey)?.currentPage) + 1),
                            });
                            return newMap;
                        });
                    }
                },
                loading: isFetchingValue,
                hasMore,
                enabled: isInfiniteScroll,
                offset: 50,
            }),
            [scrollContentId, isFetchingValue, filterKey, isInfiniteScroll, hasMore]
        )
    );

    const handleFetchSpaceTasks = (params: SpaceTaskFilters) => {
        loadingIndex.get(filterKey) === 0 &&
            setIsLoading((prev) => {
                const newMap = new Map(prev);
                newMap.set(filterKey, true);
                return newMap;
            });

        setIsFetching((prev) => {
            const newMap = new Map(prev);
            newMap.set(filterKey, true);
            return newMap;
        });

        dispatch(spaceActions.fetchSpaceTasks(spaceId, params));
    };

    // Reset list and pagination when filters change
    useEffect(() => {
        if (!skip) {
            setTasksList(new Map([[filterKey, { tasks: [], stats: null }]]));
            setPagination(new Map([[filterKey, { currentPage: "1", noOfPages: "" }]]));
            setIsFetching(new Map([[filterKey, false]]));
        }
    }, [filters, skip]);

    useEffect(() => {
        if (!skip) {
            const params = {
                page: Number(pagination.get(filterKey)?.currentPage),
                limit: Number(pagination.get(filterKey)?.noOfPages),
                ...filters,
            };
            handleFetchSpaceTasks(params);
        }
    }, [filters, skip, pagination]);

    useEffect(() => {
        if (Boolean(fetchedSpaceTasksSuccess)) {
            setTasksList((prev) => {
                const newMap = new Map(prev);
                const newTasks = fetchedSpaceTasksSuccess?.data?.tasks || [];
                const previousData = prev.get(filterKey) as unknown as {
                    tasks: SpaceTask[];
                    stats: any;
                };
                const currentList = [...(previousData?.tasks || [])];
                const currentPage = Number(pagination.get(filterKey)?.currentPage);
                const newValues = {
                    tasks: newTasks as SpaceTask[],
                    stats: fetchedSpaceTasksSuccess?.data?.stats,
                };

                // For first page, replace the entire list to handle order changes
                if (currentPage === 1) {
                    newMap.set(filterKey, newValues);
                    return newMap;
                }

                // For subsequent pages, merge while preserving order from API
                const updatedList = [...currentList];

                newTasks.forEach((newTask) => {
                    const existingIndex = updatedList.findIndex(
                        (existing) => existing._id === newTask._id
                    );

                    if (existingIndex !== -1) {
                        // Update existing item
                        updatedList[existingIndex] = newTask;
                    } else {
                        // Add new item
                        updatedList.push(newTask);
                    }
                });

                newMap.set(filterKey, {
                    tasks: updatedList,
                    stats: fetchedSpaceTasksSuccess?.data?.stats,
                });

                return newMap;
            });
        }
    }, [fetchedSpaceTasksSuccess]);

    const refetch = () => {
        const params = {
            limit: Number(pagination.get(filterKey)?.noOfPages),
            ...filters,
            page: 1,
        };
        handleFetchSpaceTasks(params);
    };

    useEffect(() => {
        if (Boolean(createdSpaceTaskSuccess)) {
            refetch();
        }
    }, [createdSpaceTaskSuccess]);

    // useEffect(() => {
    //     if (Boolean(updatedSpaceTaskSuccess)) {
    //         refetch();
    //     }
    // }, [updatedSpaceTaskSuccess, refetch]);

    // useEffect(() => {
    //     if (Boolean(deletedSpaceTaskSuccess)) {
    //         setTasksList((prev) => {
    //             const newMap = new Map(prev);
    //             newMap.set(filterKey, {
    //                 tasks: prev
    //                     .get(filterKey)
    //                     ?.tasks?.filter(
    //                         (task) => task._id !== deletedSpaceTaskSuccess?.data?.task?._id
    //                     ),
    //                 stats: prev.get(filterKey)?.stats,
    //             });
    //             return newMap;
    //         });
    //     }
    // }, [deletedSpaceTaskSuccess, filterKey]);

    useEffect(() => {
        if (!fetchingSpaceTasks && !!fetchedSpaceTasksSuccess) {
            loadingIndex.get(filterKey) === 1 &&
                setIsLoading((prev) => {
                    const newMap = new Map(prev);
                    newMap.set(filterKey, false);
                    return newMap;
                });
            setLoadingIndex((prev) => {
                const newMap = new Map(prev);
                newMap.set(filterKey, prev.get(filterKey) + 1);
                return newMap;
            });
        }
    }, [fetchingSpaceTasks, fetchedSpaceTasksSuccess]);

    useEffect(() => {
        setIsFetching((prev) => {
            const newMap = new Map(prev);
            newMap.set(filterKey, fetchingSpaceTasks);
            return newMap;
        });
    }, [fetchingSpaceTasks]);

    useEffect(() => {
        if (fetchedSpaceTasksFailure) {
            setIsFetching((prev) => {
                const newMap = new Map(prev);
                newMap.set(filterKey, false);
                return newMap;
            });
        }
    }, [fetchedSpaceTasksFailure]);

    useEffect(() => {
        setIsSuccess((prev) => {
            const newMap = new Map(prev);
            newMap.set(filterKey, fetchedSpaceTasksSuccess);
            return newMap;
        });
    }, [fetchedSpaceTasksSuccess]);

    return {
        isSuccess: Boolean(fetchedSpaceTasksSuccess),
        isError: Boolean(fetchedSpaceTasksFailure),
        isLoading: isLoading.get(filterKey) || false,
        isFetching: isFetching.get(filterKey) || false,
        data: refetchOnMount
            ? fetchedSpaceTasksSuccess?.data || []
            : tasksList.get(filterKey) || [],
        error: fetchedSpaceTasksFailure,
        pagination: pagination.get(filterKey) || { currentPage: "1", noOfPages: "" },
        refetch,
    };
};

export default useGetSpaceTasks;
